import { IMobileInventoryCountDetailDto } from './IMobileInventoryCountDetailDto';
import {
  filterPullableItems,
  filterReturnableItems,
} from '../utils/inventory-pull-return-filter.utils';

export class IMobileInventoryGroup {
  groupId: string;
  groupName: string;
  _items?: IMobileInventoryCountDetailDto[];
  _itemsPulled?: IMobileInventoryCountDetailDto[];
  _itemsReturned?: IMobileInventoryCountDetailDto[];
  mode?: string;
  notesOnly = false;
  filterQuery = '';

  get items() {
    let items: IMobileInventoryCountDetailDto[] = [];
    if (!this.notesOnly) {
      if (this.mode === 'pull') {
        items = this._itemsPulled;
      } else if (this.mode === 'return') {
        items = this._itemsReturned;
      } else {
        items = this._items;
      }
    } else {
      if (this.mode === 'pull') {
        items = this._itemsPulled.filter(this.noteFilter);
      } else if (this.mode === 'return') {
        items = this._itemsReturned.filter(this.noteFilter);
      } else {
        items = this._items.filter(this.noteFilter);
      }
    }

    if (this.filterQuery) {
      items = items.filter(
        (item) =>
          item.itemName.toLowerCase().indexOf(this.filterQuery.toLowerCase()) >
            -1 ||
          item.manufacturerPartNumber
            .toLowerCase()
            .indexOf(this.filterQuery.toLowerCase()) > -1
      );
    }
    return items;
  }

  set items(items: IMobileInventoryCountDetailDto[]) {
    this._items = items;
    this._itemsPulled = filterPullableItems(this._items);
    this._itemsReturned = filterReturnableItems(this._items);
  }

  noteFilter(item: IMobileInventoryCountDetailDto) {
    if (item.countNotes) {
      return true;
    } else if (item.inventoryCountDetailUniqueItems) {
      if (
        item.inventoryCountDetailUniqueItems
          .map((uniqueItem) => uniqueItem.countNotes)
          .filter((countNotes) => countNotes).length > 0
      ) {
        return true;
      }
    }
    return false;
  }

  get short() {
    if (this.mode === 'pull') {
      const totalShort = this.items.reduce((prev, curr) => {
        if (curr.recommendedInventoryLevel !== 0)
          return prev + (curr.short - curr.pulled);
        else return prev;
      }, 0);
      if (totalShort >= 0) return totalShort;
      return 0;
    } else {
      const totalShort = this.items
        .map((item) => item.short)
        .reduce((prev, curr) => prev + curr, 0);
      if (totalShort >= 0) return totalShort;
      return 0;
    }
  }

  get over() {
    if (this.mode === 'return') {
      const totalOver = this.items.reduce((prev, curr) => {
        return prev + (curr.over - curr.returned);
      }, 0);
      if (totalOver >= 0) return totalOver;
      return 0;
    } else {
      const totalOver = this.items
        .map((item) => item.over)
        .reduce((prev, curr) => prev + curr, 0);
      if (totalOver >= 0) return totalOver;
      return 0;
    }
  }

  get broken() {
    return this.items
      .map((item) => item.broken)
      .reduce((prev, curr) => prev + curr, 0);
  }

  get tethering() {
    return this.items
      .map((item) => item.tetheringNeeded)
      .reduce((prev, curr) => prev + curr, 0);
  }

  get countsStarted() {
    const countsStarted = this.items.reduce((prev, curr) => {
      if (curr.initialCount !== null) return prev + 1;
      return prev;
    }, 0);
    return countsStarted ? countsStarted : 0;
  }

  get pullsStarted() {
    const pullsStarted = this._itemsPulled.reduce((prev, curr) => {
      if (curr.pulled !== null && curr.recommendedInventoryLevel !== 0)
        return prev + 1;
      return prev;
    }, 0);
    return pullsStarted ? pullsStarted : 0;
  }

  get returnsStarted() {
    const returnsStarted = this._itemsReturned.reduce((prev, curr) => {
      if (curr.returned !== null) return prev + 1;
      return prev;
    }, 0);
    return returnsStarted ? returnsStarted : 0;
  }

  get pulledItems() {
    const pulledItems = this._itemsPulled.reduce((prev, curr) => {
      if (curr.recommendedInventoryLevel !== 0) return prev + 1;
      return prev;
    }, 0);
    return pulledItems ? pulledItems : 0;
  }

  get completePercent(): number {
    if (this.mode === 'pull') {
      const pulledCount = this.items.reduce((prev, curr) => {
        if (curr.pulled !== null && curr.recommendedInventoryLevel !== 0)
          return prev + 1;
        return prev;
      }, 0);
      const shortCount = this.items.reduce((prev, curr) => {
        if (curr.short !== null && curr.recommendedInventoryLevel !== 0)
          return prev + 1;
        return prev;
      }, 0);
      if (shortCount === 0) return 100;
      else return (pulledCount / shortCount) * 100;
    } else if (this.mode === 'return') {
      const returnedCount = this.items.reduce((prev, curr) => {
        if (curr.returned !== null) return prev + 1;
        return prev;
      }, 0);
      const overCount = this.items.reduce((prev, curr) => {
        if (curr.over !== null) return prev + 1;
        return prev;
      }, 0);
      return (returnedCount / overCount) * 100;
    } else {
      return (this.countsStarted / this.items.length) * 100;
    }
  }

  /* TODO: make all InventoryCount API get this info and not require it on front end
  get counters() {
    return this.items.map((item) => item.inventoryCounters).flat();
  }
  */

  async updateItem(updatedItem: IMobileInventoryCountDetailDto) {
    const itemIndex = this._items.findIndex((item) => {
      return item.inventoryCountDetailId === updatedItem.inventoryCountDetailId;
    });

    // Update stats
    updatedItem = await this.updateStats(updatedItem);

    this._items[itemIndex] = Object.assign(this._items[itemIndex], updatedItem);

    const indexPulled = this._itemsPulled.findIndex(
      (item) =>
        item.inventoryCountDetailId === updatedItem.inventoryCountDetailId
    );
    const indexReturned = this._itemsReturned.findIndex(
      (item) =>
        item.inventoryCountDetailId === updatedItem.inventoryCountDetailId
    );

    if (indexPulled >= 0) {
      this._itemsPulled[indexPulled] = Object.assign(
        this._itemsPulled[indexPulled],
        updatedItem
      );
    }

    if (indexReturned >= 0) {
      this._itemsReturned[indexReturned] = Object.assign(
        this._itemsReturned[indexReturned],
        updatedItem
      );
    }
  }

  async updateStats(
    updatedItem: IMobileInventoryCountDetailDto
  ): Promise<IMobileInventoryCountDetailDto> {
    if (!this.mode && updatedItem.hasSerialNumber) {
      const reset = updatedItem.inventoryCountDetailUniqueItems.every(
        (uniqueItem) => {
          return uniqueItem.isFound === null;
        }
      );

      const countsStarted = updatedItem.inventoryCountDetailUniqueItems.filter(
        (item) => {
          return item.isFound;
        }
      ).length;

      if (reset) {
        updatedItem.initialCount = null;
        updatedItem.short = 0;
        updatedItem.over = 0;
        return;
      } else {
        updatedItem.initialCount = countsStarted;
      }

      let short =
        updatedItem.recommendedInventoryLevel - updatedItem.initialCount;
      if (short < 0) short = 0;

      updatedItem.short = short;

      let over =
        updatedItem.initialCount - updatedItem.recommendedInventoryLevel;
      if (over < 0) over = 0;

      updatedItem.over = over;
    } else if (this.mode === 'pull' && updatedItem.hasSerialNumber) {
      const reset = updatedItem.inventoryCountDetailUniqueItems.every(
        (uniqueItem) => {
          return uniqueItem.isPulled === null;
        }
      );

      const pullsStarted = updatedItem.inventoryCountDetailUniqueItems.filter(
        (item) => {
          return item.isPulled;
        }
      ).length;

      if (reset) {
        updatedItem.pulled = null;
      } else {
        updatedItem.pulled = pullsStarted;
      }
    } else if (this.mode === 'return' && updatedItem.hasSerialNumber) {
      const reset = updatedItem.inventoryCountDetailUniqueItems.every(
        (uniqueItem) => {
          return uniqueItem.isReturned === null;
        }
      );

      const returnsStarted = updatedItem.inventoryCountDetailUniqueItems.filter(
        (item) => {
          return item.isReturned;
        }
      ).length;

      if (reset) {
        updatedItem.returned = null;
      } else {
        updatedItem.returned = returnsStarted;
      }
    }

    return updatedItem;
  }
}
